import { Component, OnInit, ViewChild, ElementRef, Inject, Optional } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { NotificationService } from 'src/app/services/notification.service';
import { TranslocoModule, TranslocoService } from '@jsverse/transloco';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';

import { JwtService } from 'src/app/services/jwt.service';
import { BackendService } from 'src/app/services/backend.service';
import { UserSessionService } from 'src/app/services/user-session.service';
import { MatProgressBarModule } from '@angular/material/progress-bar';

/** Optional context for the support dialog. */
export interface DialogSupportData {
  /** Why the user is asked to write a ticket, shown above the form (already translated). */
  reason?: string;
}

@Component({
  selector: 'app-dialog-support',
  templateUrl: './dialog-support.component.html',
  imports: [
    MatDialogModule,
    MatButtonModule,
    MatIconModule,
    ReactiveFormsModule,
    TranslocoModule,
    MatFormFieldModule,
    MatInputModule,
    MatProgressBarModule,
    MatSelectModule
  ]
})
export class DialogSupportComponent implements OnInit {
  @ViewChild('fileInput') fileInput: ElementRef<HTMLInputElement>;
  
  supportData = new FormGroup({
    email: new FormControl<string>('', [Validators.email]),
    body: new FormControl<string>('', [Validators.required]),
    subject: new FormControl<string>('', [Validators.required])
  });
  provideEmail: boolean;
  isSending = false;
  selectedFiles: File[] = [];
  readonly maxFiles = 5;

  constructor(
    public dialogRef: MatDialogRef<DialogSupportComponent>,
    private backendService: BackendService,
    private readonly jwtService: JwtService,
    private readonly userSessionService: UserSessionService,
    private notify: NotificationService,
    private transloco: TranslocoService,
    @Optional() @Inject(MAT_DIALOG_DATA) public readonly data: DialogSupportData | null
  ) {}

  ngOnInit(): void {
    if (!this.jwtService.isLoggedIn) {
      this.supportData.get('email').setValidators([Validators.required, Validators.email]);
      this.provideEmail = true;
    } else {
      this.userSessionService.ensureLoaded().subscribe(() => {
        this.supportData.get('email').setValue(this.userSessionService.email);
      });
    }
  }

  handleFileInput(files: FileList): void {
    if (!files?.length) return;
    const remaining = this.maxFiles - this.selectedFiles.length;
    for (let i = 0; i < Math.min(files.length, remaining); i++) {
      this.selectedFiles.push(files[i]);
    }
  }

  resetFileInput(): void {
    if (this.fileInput) {
      this.fileInput.nativeElement.value = null;
    }
  }

  removeFile(index: number): void {
    this.selectedFiles.splice(index, 1);
  }

  async onCloseClicked(): Promise<void> {
    if (!this.hasUserInput()) {
      this.dialogRef.close();
      return;
    }

    const confirmed = await this.notify.confirmDialog({
      title: this.transloco.translate('support.close_confirm.title'),
      body: this.transloco.translate('support.close_confirm.body')
    });
    if (confirmed) {
      this.dialogRef.close();
    }
  }

  onCreateClicked() {
    this.isSending = true;

    if (this.selectedFiles.length) {
      this.createTicketWithFile();
    } else {
      this.createTicketWithoutFile();
    }
  }

  private hasUserInput(): boolean {
    const subject = this.supportData.get('subject')?.value?.trim();
    const body = this.supportData.get('body')?.value?.trim();
    const emailEntered = this.provideEmail && !!this.supportData.get('email')?.value?.trim();
    return !!subject || !!body || emailEntered || this.selectedFiles.length > 0;
  }

  private createTicketWithFile(): void {
    const formData = new FormData();
    for (const file of this.selectedFiles) {
      formData.append('files', file);
    }
    formData.append('subject', this.supportData.get('subject').value);
    formData.append('body', this.supportData.get('body').value);
    formData.append('email', this.supportData.get('email').value);
    formData.append('userAgent', navigator.userAgent);
    formData.append('currentUri', window.location.href);

    // Use upload method for FormData
    this.backendService
      .post<'auth/generateNonce'>('auth/{id}/nonce', {
        path: {
          id: this.jwtService.role.id
        }
      })
      .subscribe((res) => {
        this.backendService
          .upload<'support/createTicketWithFile'>('support/ticket', {
            body: formData as any,
            query: {
              nonce: res.nonce
            }
          })
          .subscribe({
            next: (r) => {
              this.isSending = false;
              this.notify.success(this.transloco.translate('support.ticket_success', { key: r.supportTicketKey }));
              this.dialogRef.close();
            },
            error: () => {
              this.isSending = false;
            }
          });
      });
  }

  private createTicketWithoutFile(): void {
    // Use JSON for requests without files
    this.backendService
      .put<'support/createTicket'>('support/ticket', {
        body: {
          subject: this.supportData.get('subject').value,
          body: this.supportData.get('body').value,
          email: this.supportData.get('email').value,
          userAgent: navigator.userAgent,
          currentUri: window.location.href
        }
      })
      .subscribe({
        next: (res) => {
          this.isSending = false;
          this.notify.success(this.transloco.translate('support.ticket_success', { key: res.supportTicketKey }));
          this.dialogRef.close();
        },
        error: () => {
          this.isSending = false;
        }
      });
  }
}
